// The 5 reports design 5.5 asks for ("Progress by cohort, attendance and the guarantee, watch time
// per recording, logins, and access by status. All export to CSV."), each a hub.report_* read
// (docs/data-model.md 10.4.2). The reads leave out comp and test access, test logins and products in
// shadow, and name an anonymised person "Deleted person"; none returns an amount. The guarantee is
// the Accelerator's, which moves in phase 2, so attendance is attendance only in phase 1.
//
// Pure: the definitions, the words and the CSV columns, checked on their own.
import { csvDate } from '../ui/csv.js'

const TZ_PARTS = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' })
// a timestamp's London day, YYYY-MM-DD
export function londonDay(t) {
  if (!t) return ''
  const o = {}
  for (const p of TZ_PARTS.formatToParts(new Date(t))) o[p.type] = p.value
  return `${o.year}-${o.month}-${o.day}`
}

// An anonymised person's placeholder address isn't an address: shown and exported as blank
export const realEmail = e => (e && !/@deleted\.invalid$/i.test(e) ? e : '')
export const isDeleted = r => /@deleted\.invalid$/i.test(r?.email || '') || r?.full_name === 'Deleted person'

const STATUS = {
  secured: 'Place secured', active: 'Active', overdue: 'Overdue', suspended: 'Suspended', paused: 'Paused', ended: 'Ended', revoked: 'Revoked',
}
export const statusWord = s => STATUS[s] || (s ? s[0].toUpperCase() + s.slice(1) : '')
const SOURCE = { stripe: 'Bought', manual: 'Paid another way', import: 'Imported', comp: 'Free', test: 'Test' }
export const sourceWord = s => SOURCE[s] || s || ''
const MARK = { present: 'Present', absent: 'Absent', excused: 'Excused' }
export const markWord = s => MARK[s] || 'Not marked'
const END = {
  subscription_ended: 'Subscription ended', plan_cancelled: 'Payment plan cancelled', refund_ended: 'Ended after a refund',
  revoked_by_staff: 'Revoked', batch_undone: 'Bulk grant undone', expired: 'Reached its end date', retired: 'Product retired',
  merged: 'Merged into another person',
}
export const endWord = s => END[s] || (s ? s.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : '')

// "1 hour 48 minutes" is long in a table: "1h 48m", "52m"
export function hoursMinutes(seconds) {
  const total = Math.round((Number(seconds) || 0) / 60)
  const h = Math.floor(total / 60), m = total % 60
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`
}

export const ofTotal = (done, total) => `${done ?? 0} of ${total ?? 0}`

export const ACCESS_STATUSES = ['secured', 'active', 'overdue', 'suspended', 'paused', 'ended', 'revoked']

// key: the address (/admin/reports/<key>); read: the hub function; filters: which the screen offers
export const REPORTS = [
  {
    key: 'progress', label: 'Progress by cohort', read: 'report_progress_by_cohort', noun: ['person', 'people'],
    filters: ['product', 'cohort'], file: 'hub-progress-by-cohort',
    line: 'Everyone with a product in the hub, by cohort, with what each person has watched, ticked and finished.',
    args: f => ({ product: f.product || undefined, cohort: f.cohort || undefined }),
    csv: [
      { key: 'full_name', label: 'Name' }, { label: 'Email', value: r => realEmail(r.email) },
      { key: 'product_name', label: 'Product' }, { key: 'cohort_name', label: 'Cohort' },
      { label: 'Status', value: r => statusWord(r.status) }, { label: 'How it was paid for', value: r => sourceWord(r.source) },
      { key: 'starts_at', label: 'Started', format: 'date' },
      { key: 'recordings_watched', label: 'Recordings watched' }, { key: 'recordings_total', label: 'Recordings' },
      { key: 'homework_done', label: 'Homework done' }, { key: 'homework_total', label: 'Homework' },
      { key: 'steps_done', label: 'Steps done' }, { key: 'steps_total', label: 'Steps' },
      { key: 'last_played_at', label: 'Last watched', format: 'datetime' }, { key: 'finished_at', label: 'Finished', format: 'date' },
      { key: 'certificate_number', label: 'Certificate' },
    ],
  },
  {
    key: 'attendance', label: 'Attendance', read: 'report_attendance', noun: ['register line', 'register lines'],
    filters: ['product', 'dates'], file: 'hub-attendance',
    line: "Each live session that's been held, and everyone expected at it, with what the register says.",
    args: f => ({ product: f.product || undefined, from: f.from || undefined, to: f.to || undefined }),
    csv: [
      { key: 'starts_at', label: 'Session date', format: 'datetime' }, { key: 'product_name', label: 'Product' },
      { key: 'cohort_name', label: 'Cohort' }, { key: 'module_label', label: 'Week' }, { key: 'session_title', label: 'Session' },
      { key: 'full_name', label: 'Name' }, { label: 'Email', value: r => realEmail(r.email) },
      { label: 'Register', value: r => markWord(r.status) }, { label: 'Taken', value: r => (r.source === 'manual' ? 'By hand' : '') },
      { key: 'note', label: 'Note' },
    ],
  },
  {
    key: 'watch-time', label: 'Watch time', read: 'report_watch_time', noun: ['recording', 'recordings'],
    filters: ['product'], file: 'hub-watch-time',
    line: 'Every recording, with how many have started it, how many watched it to the end, and the time spent.',
    args: f => ({ product: f.product || undefined }),
    csv: [
      { key: 'product_name', label: 'Product' }, { key: 'module_label', label: 'Week or module' }, { key: 'title', label: 'Recording' },
      { key: 'part', label: 'Part' }, { key: 'is_published', label: 'Switched on', format: 'yesno' },
      { label: 'Length (minutes)', value: r => (r.duration_seconds ? Math.round(r.duration_seconds / 60) : '') },
      { key: 'viewers', label: 'Started it' }, { key: 'watched', label: 'Watched it' },
      { label: 'Watch time (minutes)', value: r => Math.round((Number(r.seconds_watched) || 0) / 60) },
    ],
  },
  {
    key: 'logins', label: 'Logins', read: 'report_logins', noun: ['person', 'people'],
    filters: ['product', 'dates'], file: 'hub-logins',
    line: 'Everyone with a product in the hub, with how often each person has logged in, and how.',
    args: f => ({ product: f.product || undefined, from: f.from || undefined, to: f.to || undefined }),
    csv: [
      { key: 'full_name', label: 'Name' }, { label: 'Email', value: r => realEmail(r.email) }, { key: 'products', label: 'Products' },
      { key: 'logins', label: 'Logins' }, { key: 'code_logins', label: 'With a code' }, { key: 'password_logins', label: 'With a password' },
      { key: 'last_login_at', label: 'Last login', format: 'datetime' }, { key: 'last_login_device', label: 'Last device' },
    ],
  },
  {
    key: 'access', label: 'Access by status', read: 'report_access_by_status', noun: ['access row', 'access rows'],
    filters: ['product', 'status'], file: 'hub-access-by-status',
    line: 'Every access row that counts, at any status, with its dates.',
    args: f => ({ product: f.product || undefined, status: f.status || undefined }),
    csv: [
      { key: 'full_name', label: 'Name' }, { label: 'Email', value: r => realEmail(r.email) },
      { key: 'product_name', label: 'Product' }, { key: 'cohort_name', label: 'Cohort' },
      { label: 'Status', value: r => statusWord(r.status) }, { label: 'How it was paid for', value: r => sourceWord(r.source) },
      { key: 'starts_at', label: 'Started', format: 'date' }, { key: 'ends_at', label: 'Ends', format: 'date' },
      { key: 'ended_at', label: 'Ended', format: 'date' }, { label: 'Why it ended', value: r => endWord(r.end_reason) },
      { key: 'balance_due_at', label: 'Balance due', format: 'date' }, { key: 'suspend_on', label: 'Suspends on', format: 'date' },
      { key: 'paused_until', label: 'Paused until', format: 'date' }, { label: 'Kept open until at least', value: r => csvDate(r.promised_until) },
    ],
  },
]

export const reportByKey = key => REPORTS.find(r => r.key === key) || null

// What the screen shows of a long report: the first 100 rows (the CSV has every row)
export const SHOWN = 100

export const LEFT_OUT = 'Free grants, test logins and products not in the hub yet are left out, so these count what was paid for. No amounts: those stay in Stripe.'
